<template>
  <div>
    <div class="page-head">
      <div>
        <h2>月度结账</h2>
        <p>先结各分部，再结公司；反结账顺序相反且必须填写原因</p>
      </div>
      <el-button
        type="primary"
        @click="dialog = true"
      >
        发起结账
      </el-button>
    </div>
    <el-card shadow="never">
      <el-table
        :data="rows"
        v-loading="loading"
      >
        <el-table-column
          prop="close_month"
          label="月份"
        />
        <el-table-column label="范围">
          <template #default="{ row }">
            {{ row.branch_name || '公司汇总' }}
          </template>
        </el-table-column>
        <el-table-column
          prop="status"
          label="状态"
        >
          <template #default="{ row }">
            <StatusTag :status="row.status" />
          </template>
        </el-table-column>
        <el-table-column
          prop="closed_by_name"
          label="结账人"
        />
        <el-table-column
          prop="closed_at"
          label="结账时间"
        />
        <el-table-column
          prop="note"
          label="说明"
        />
        <el-table-column label="操作">
          <template #default="{ row }">
            <el-button
              v-if="row.status === 'CLOSED'"
              link
              type="danger"
              @click="reopen(row)"
            >
              反结账
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
    <el-dialog
      v-model="dialog"
      title="月度结账"
      width="560px"
    >
      <el-form label-width="90px">
        <el-form-item
          label="月份"
          required
        >
          <el-date-picker
            v-model="form.month"
            type="month"
            value-format="YYYY-MM"
          />
        </el-form-item>
        <el-form-item label="结账范围">
          <el-radio-group v-model="scope">
            <el-radio value="branch">分部</el-radio>
            <el-radio value="company">公司汇总</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item
          v-if="scope === 'branch'"
          label="分部"
          required
        >
          <el-select v-model="form.branchId">
            <el-option
              v-for="b in branches"
              :key="b.id"
              :label="b.name"
              :value="b.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="说明">
          <el-input
            v-model="form.note"
            type="textarea"
          />
        </el-form-item>
        <el-alert
          v-if="check"
          :type="check.canClose ? 'success' : 'error'"
          :closable="false"
          :title="
            check.canClose ? '检查通过，可以结账' : '存在未处理事项，暂不能结账'
          "
        >
          <template #default>
            成交草稿 {{ check.draftTransactions || 0 }}，收款草稿
            {{ check.draftReceipts || 0 }}，收入草稿
            {{ check.draftIncome || 0 }}，支出草稿
            {{ check.draftExpenses || 0 }}，发放草稿
            {{ check.draftPayouts || 0 }}，调整草稿
            {{ check.draftAdjustments || 0 }}，异常账户
            {{ check.negativeAccounts || 0 }}
          </template>
        </el-alert>
      </el-form>
      <template #footer>
        <el-button @click="precheck">结账前检查</el-button>
        <el-button
          type="primary"
          :disabled="!check?.canClose"
          :loading="saving"
          @click="close"
        >
          确认结账
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>
<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ledgerApi, organizationApi } from '../api'
import StatusTag from '../components/StatusTag.vue'
const rows = ref([]),
  branches = ref([]),
  loading = ref(false),
  dialog = ref(false),
  saving = ref(false),
  scope = ref('branch'),
  check = ref(null),
  form = reactive({ month: '', branchId: null, note: '' })
watch(
  [() => form.month, () => form.branchId, scope],
  () => (check.value = null)
)
const load = async () => {
  loading.value = true
  try {
    const result = await Promise.all([
      ledgerApi.monthCloses(),
      organizationApi.branches(),
    ])
    rows.value = result[0]
    branches.value = result[1]
  } finally {
    loading.value = false
  }
}
const args = () => ({
  month: form.month,
  branchId: scope.value === 'branch' ? form.branchId : undefined,
})
const precheck = async () => {
  if (!form.month || (scope.value === 'branch' && !form.branchId))
    return ElMessage.warning('请选择月份和结账范围')
  try {
    check.value = await ledgerApi.precheckMonth(args())
  } catch (e) {
    ElMessage.error(e?.message || '检查失败')
  }
}
const close = async () => {
  saving.value = true
  try {
    await ledgerApi.closeMonth({ ...args(), note: form.note })
    dialog.value = false
    check.value = null
    ElMessage.success('结账完成')
    load()
  } catch (e) {
    ElMessage.error(e?.message || '结账失败')
  } finally {
    saving.value = false
  }
}
const reopen = async (r) => {
  try {
    const { value } = await ElMessageBox.prompt('请输入反结账原因', '反结账', {
      inputValidator: (v) => !!v?.trim() || '原因不能为空',
    })
    await ledgerApi.reopenMonth(r.id, value)
    load()
  } catch (e) {
    if (e !== 'cancel') ElMessage.error(e?.message || '反结账失败')
  }
}
onMounted(() => {
  form.month = new Date().toISOString().slice(0, 7)
  load()
})
</script>
<style scoped>
.page-head {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-head h2 {
  font-size: 24px;
}
.page-head p {
  color: #8492a6;
  margin-top: 6px;
}
.el-card {
  border: 0;
}
</style>
